import type { Middleware } from '@reduxjs/toolkit';

/**
 * Middleware that prevents recursive dispatch calls from overflowing the call stack.
 *
 * With many RTK Query API slices (~90+), each adding its own middleware to the Redux
 * store, the middleware chain becomes extremely deep. When the first action is dispatched,
 * each RTK Query middleware initialises by synchronously dispatching a `middlewareRegistered`
 * action through `store.dispatch`. Because every such dispatch re-enters the full middleware
 * chain, middleware N's initialisation triggers middleware N+1's initialisation recursively,
 * producing O(N²) call-stack depth. At ~90 slices this exceeds the browser's maximum call
 * stack size (~10-15 k frames in Chrome) and throws `RangeError: Maximum call stack size
 * exceeded`.
 *
 * This middleware is placed **before** all RTK Query middleware. It detects re-entrant
 * dispatches (a dispatch that occurs while another dispatch is already being processed)
 * and queues them instead of letting them recurse. Once the outermost dispatch finishes,
 * the queued actions are drained iteratively via `next()`, keeping the
 * call-stack depth at O(N) per action.
 *
 * @see https://finch-h0.sentry.io/issues/PROD-REACT-63
 */
export function createDispatchGuardMiddleware(): Middleware {
  let isProcessing = false;
  const queue: unknown[] = [];

  return (storeApi) => (next) => (action) => {
    if (isProcessing) {
      // Already inside a dispatch – queue this action to avoid recursive stack growth.
      queue.push(action);
      return action;
    }

    isProcessing = true;
    try {
      return next(action);
    } finally {
      // Drain any actions that were queued during the dispatch above.
      // isProcessing stays true so any re-entrant dispatches triggered
      // during drain are still queued rather than recursing. We call
      // next() directly so queued actions traverse downstream middleware
      // (RTK Query etc.) without re-entering this guard, keeping the
      // call-stack depth iterative / O(N).
      while (queue.length > 0) {
        const queued = queue.shift();
        if (queued !== undefined) {
          try {
            next(queued);
          } catch (e) {
            // Log but don't propagate – prevent a queued action's error from
            // overwriting the original dispatch's return value or orphaning
            // remaining queued actions.
            console.error(
              '[dispatchGuardMiddleware] Error dispatching queued action:',
              e,
            );
          }
        }
      }
      isProcessing = false;
    }
  };
}
